import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { cn, getInitials } from '../lib/utils';
import { Users, Play, ArrowLeft, RefreshCw, Crown, Loader2, X, Check, RotateCcw, Hand } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Domino Piece Component
const DominoPiece = ({ piece, onClick, selected, disabled, small }) => {
  const isDouble = piece.left === piece.right;
  
  const renderDots = (count, section) => {
    const dotPositions = {
      0: [],
      1: [{ x: 50, y: 50 }],
      2: [{ x: 25, y: 25 }, { x: 75, y: 75 }],
      3: [{ x: 25, y: 25 }, { x: 50, y: 50 }, { x: 75, y: 75 }],
      4: [{ x: 25, y: 25 }, { x: 75, y: 25 }, { x: 25, y: 75 }, { x: 75, y: 75 }],
      5: [{ x: 25, y: 25 }, { x: 75, y: 25 }, { x: 50, y: 50 }, { x: 25, y: 75 }, { x: 75, y: 75 }],
      6: [{ x: 25, y: 25 }, { x: 75, y: 25 }, { x: 25, y: 50 }, { x: 75, y: 50 }, { x: 25, y: 75 }, { x: 75, y: 75 }]
    };
    
    const dots = dotPositions[count] || [];
    const dotSize = small ? 4 : 6;
    
    return dots.map((pos, idx) => (
      <circle
        key={`${section}-${idx}`}
        cx={pos.x}
        cy={pos.y}
        r={dotSize}
        fill="currentColor"
      />
    ));
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative transition-all duration-200",
        small ? "w-12 h-6" : "w-20 h-10",
        isDouble && (small ? "w-6 h-12" : "w-10 h-20"),
        "bg-white rounded-lg shadow-md border-2",
        selected ? "border-pink-500 ring-2 ring-pink-500/50 scale-105" : "border-stone-300",
        disabled ? "opacity-50 cursor-not-allowed" : "hover:scale-105 hover:shadow-lg cursor-pointer",
        "text-stone-800"
      )}
      data-testid={`domino-piece-${piece.id}`}
    >
      {isDouble ? (
        // Vertical domino for doubles
        <div className="flex flex-col h-full">
          <svg viewBox="0 0 100 100" className="flex-1">
            {renderDots(piece.left, 'top')}
          </svg>
          <div className="h-0.5 bg-stone-400 mx-1" />
          <svg viewBox="0 0 100 100" className="flex-1">
            {renderDots(piece.right, 'bottom')}
          </svg>
        </div>
      ) : (
        // Horizontal domino
        <div className="flex h-full">
          <svg viewBox="0 0 100 100" className="flex-1">
            {renderDots(piece.left, 'left')}
          </svg>
          <div className="w-0.5 bg-stone-400 my-1" />
          <svg viewBox="0 0 100 100" className="flex-1">
            {renderDots(piece.right, 'right')}
          </svg>
        </div>
      )}
    </button>
  );
};

// Board Component
const GameBoard = ({ board, leftEnd, rightEnd, onPlayPosition }) => {
  if (!board || board.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground">
        <p>Pwemye jwè pou jwe yon domino</p>
      </div>
    );
  }
  
  return (
    <div className="relative">
      {/* Play Position Indicators */}
      <div className="flex items-center justify-center gap-2 flex-wrap py-4 overflow-x-auto">
        {/* Left play button */}
        <button
          onClick={() => onPlayPosition('left')}
          className="w-16 h-8 border-2 border-dashed border-pink-400 rounded-lg flex items-center justify-center text-pink-400 hover:bg-pink-500/10 transition-all"
          data-testid="play-left-btn"
        >
          <span className="text-xs font-bold">{leftEnd}</span>
        </button>
        
        {/* Board pieces */}
        {board.map((piece, idx) => (
          <div key={piece.id} className="shrink-0">
            <DominoPiece piece={piece} disabled small />
          </div>
        ))}
        
        {/* Right play button */}
        <button
          onClick={() => onPlayPosition('right')}
          className="w-16 h-8 border-2 border-dashed border-violet-400 rounded-lg flex items-center justify-center text-violet-400 hover:bg-violet-500/10 transition-all"
          data-testid="play-right-btn"
        >
          <span className="text-xs font-bold">{rightEnd}</span>
        </button>
      </div>
    </div>
  );
};

// Player Info Component
const PlayerCard = ({ player, isCurrentTurn, isMe }) => (
  <div 
    className={cn(
      "flex items-center gap-2 p-2 rounded-xl transition-all",
      isCurrentTurn && "bg-gradient-to-r from-pink-500/20 to-violet-500/20 ring-2 ring-pink-500",
      isMe && "border-2 border-primary/50"
    )}
    data-testid={`player-card-${player.user_id}`}
  >
    <div className="relative">
      <Avatar className={cn(
        "w-10 h-10",
        player.team === "red" ? "ring-2 ring-red-500" : "ring-2 ring-blue-500"
      )}>
        <AvatarImage src={player.avatar} />
        <AvatarFallback className={cn(
          "text-white text-xs font-bold",
          player.team === "red" ? "bg-red-500" : "bg-blue-500"
        )}>
          {getInitials(player.username)}
        </AvatarFallback>
      </Avatar>
      {player.is_ready && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">{player.username}</p>
      <p className="text-xs text-muted-foreground">
        {player.hand_count} domino{player.hand_count !== 1 ? 's' : ''}
      </p>
    </div>
    {isCurrentTurn && (
      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
    )}
  </div>
);

// Main Game Page
export default function DominoGamePage() {
  const { user, token, language } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [game, setGame] = useState(null);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [showModeSelect, setShowModeSelect] = useState(true);
  const [availableGames, setAvailableGames] = useState([]);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  
  // Fetch game state
  const fetchGame = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/domino/my-game`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.game) {
        setGame(response.data.game);
        setShowModeSelect(false);
      }
    } catch (error) {
      console.error('Failed to fetch game:', error);
    }
  }, [token]);
  
  // Fetch available games
  const fetchAvailableGames = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/domino/games`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableGames(response.data.games || []);
    } catch (error) {
      console.error('Failed to fetch games:', error);
    }
  }, [token]);
  
  // Quick match
  const quickMatch = async (mode) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/domino/quick-match`, 
        { mode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setGame(response.data);
      setShowModeSelect(false);
      toast.success('Ou jwenn yon match!');
    } catch (error) {
      toast.error('Pa ka jwenn match');
    } finally {
      setLoading(false);
    }
  };
  
  // Join existing game
  const joinGame = async (gameId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/domino/games/${gameId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setGame(response.data);
      setShowModeSelect(false);
      toast.success('Ou antre nan jwèt la!');
    } catch (error) {
      toast.error('Pa ka antre nan jwèt');
    } finally {
      setLoading(false);
    }
  };
  
  // Leave game
  const leaveGame = async () => {
    if (!game) return;
    try {
      await axios.post(`${API}/domino/games/${game.game_id}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setGame(null);
      setShowModeSelect(true);
      toast.success('Ou kite jwèt la');
    } catch (error) {
      toast.error('Erè');
    }
  };
  
  // Toggle ready
  const toggleReady = async () => {
    if (!game) return;
    try {
      const response = await axios.post(`${API}/domino/games/${game.game_id}/ready`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setGame(response.data);
    } catch (error) {
      toast.error('Erè');
    }
  };
  
  // Play piece
  const playPiece = async (position) => {
    if (!game || !selectedPiece) return;
    
    try {
      const response = await axios.post(`${API}/domino/games/${game.game_id}/play`,
        { piece_id: selectedPiece.id, position },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setSelectedPiece(null);
      setGame(response.data.game);
      
      if (response.data.game_over) {
        toast.success(response.data.winner === getMyTeam() 
          ? 'Ou genyen! 🎉' 
          : 'Ou pèdi! 😢'
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pa ka jwe');
    }
  };
  
  // Pass turn
  const passTurn = async () => {
    if (!game) return;
    try {
      const response = await axios.post(`${API}/domino/games/${game.game_id}/pass`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setGame(response.data.game);
      
      if (response.data.game_over) {
        if (response.data.blocked) {
          toast.info('Jwèt bloke!');
        }
        toast.success(response.data.winner === getMyTeam() 
          ? 'Ou genyen! 🎉' 
          : 'Ou pèdi! 😢'
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ou gen mouvman valid');
    }
  };
  
  // Get my team
  const getMyTeam = () => {
    if (!game) return null;
    const myPlayer = game.players.find(p => p.user_id === user?.id);
    return myPlayer?.team;
  };
  
  // Get my hand
  const getMyHand = () => {
    if (!game) return [];
    const myPlayer = game.players.find(p => p.user_id === user?.id);
    return myPlayer?.hand || [];
  };
  
  // Check if it's my turn
  const isMyTurn = () => {
    return game?.current_player_id === user?.id;
  };
  
  // Check if piece can be played
  const canPlayPiece = (piece) => {
    if (!game || game.board.length === 0) return true;
    return piece.left === game.left_end || 
           piece.right === game.left_end ||
           piece.left === game.right_end ||
           piece.right === game.right_end;
  };
  
  // Poll for game updates
  useEffect(() => {
    if (game && game.status === 'waiting' || game?.status === 'playing') {
      pollRef.current = setInterval(() => {
        fetchGame();
      }, 2000);
    }
    
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [game?.game_id, game?.status, fetchGame]);
  
  // Initial load
  useEffect(() => {
    fetchGame();
    fetchAvailableGames();
  }, [fetchGame, fetchAvailableGames]);
  
  // Mode selection screen
  if (showModeSelect && !game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
                Domino
              </h1>
              <p className="text-sm text-muted-foreground">Jwe kont lòt moun online</p>
            </div>
          </div>
          
          {/* Game Mode Cards */}
          <div className="grid gap-4 mb-8">
            <button
              onClick={() => quickMatch('2v2')}
              disabled={loading}
              className="group relative p-6 bg-card rounded-2xl border-2 border-transparent hover:border-pink-500/50 transition-all text-left"
              data-testid="mode-2v2-btn"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-1">2 vs 2</h3>
                  <p className="text-muted-foreground">4 jwè, 2 ekip</p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500/20 to-violet-500/20 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-pink-500" />
                </div>
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Play className="w-5 h-5 text-pink-500" />
              </div>
            </button>
            
            <button
              onClick={() => quickMatch('4v4')}
              disabled={loading}
              className="group relative p-6 bg-card rounded-2xl border-2 border-transparent hover:border-violet-500/50 transition-all text-left"
              data-testid="mode-4v4-btn"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-1">4 vs 4</h3>
                  <p className="text-muted-foreground">8 jwè, 2 ekip</p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500/20 to-pink-500/20 rounded-full flex items-center justify-center">
                  <Crown className="w-8 h-8 text-violet-500" />
                </div>
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Play className="w-5 h-5 text-violet-500" />
              </div>
            </button>
          </div>
          
          {/* Available Games */}
          {availableGames.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold">Jwèt ki disponib</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchAvailableGames}
                  className="rounded-full"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {availableGames.map(g => (
                  <div 
                    key={g.game_id}
                    className="flex items-center justify-between p-3 bg-card rounded-xl"
                  >
                    <div>
                      <p className="font-medium">{g.mode}</p>
                      <p className="text-sm text-muted-foreground">
                        {g.players_count}/{g.max_players} jwè
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => joinGame(g.game_id)}
                      disabled={loading}
                    >
                      Antre
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Game screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={leaveGame}
              className="rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold">Domino {game?.mode}</h1>
              <p className="text-xs text-muted-foreground">
                {game?.status === 'waiting' && 'Ap tann jwè...'}
                {game?.status === 'playing' && (isMyTurn() ? 'Tou pa w!' : 'Ap tann...')}
                {game?.status === 'finished' && 'Jwèt fini!'}
              </p>
            </div>
          </div>
          
          {/* Scores */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {game?.scores?.red || 0}
              </div>
              <p className="text-xs text-red-500">Wouj</p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {game?.scores?.blue || 0}
              </div>
              <p className="text-xs text-blue-500">Ble</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Players */}
      <div className="p-4 border-b">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {game?.players?.map(player => (
              <PlayerCard 
                key={player.user_id}
                player={player}
                isCurrentTurn={game.current_player_id === player.user_id}
                isMe={player.user_id === user?.id}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Game Board */}
      <div className="flex-1 p-4 overflow-auto">
        <div className="max-w-4xl mx-auto">
          {game?.status === 'waiting' ? (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-pink-500 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">
                Ap tann jwè... ({game.players.length}/{game.max_players})
              </p>
              <p className="text-muted-foreground text-sm mb-6">
                Tout jwè dwe pare pou kòmanse
              </p>
              
              {/* Ready Button */}
              {(() => {
                const myPlayer = game.players.find(p => p.user_id === user?.id);
                return (
                  <Button
                    onClick={toggleReady}
                    variant={myPlayer?.is_ready ? "secondary" : "default"}
                    size="lg"
                    className="rounded-full"
                    data-testid="ready-btn"
                  >
                    {myPlayer?.is_ready ? (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Pare!
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Mwen Pare
                      </>
                    )}
                  </Button>
                );
              })()}
            </div>
          ) : game?.status === 'finished' ? (
            <div className="text-center py-12">
              <div className={cn(
                "w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center",
                game.winner === getMyTeam() 
                  ? "bg-green-500/20 text-green-500" 
                  : "bg-red-500/20 text-red-500"
              )}>
                <Crown className="w-12 h-12" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                {game.winner === getMyTeam() ? 'Ou Genyen! 🎉' : 'Ou Pèdi 😢'}
              </h2>
              <p className="text-muted-foreground mb-2">
                Ekip {game.winner === 'red' ? 'Wouj' : 'Ble'} genyen!
              </p>
              <p className="text-sm mb-6">
                Skor final: Wouj {game.scores.red} - Ble {game.scores.blue}
              </p>
              <Button 
                onClick={() => { setGame(null); setShowModeSelect(true); }}
                size="lg"
                className="rounded-full"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Jwe Ankò
              </Button>
            </div>
          ) : (
            <>
              {/* Game Board */}
              <div className="bg-green-800/20 rounded-2xl p-4 mb-4 min-h-[150px]">
                <GameBoard 
                  board={game?.board || []}
                  leftEnd={game?.left_end}
                  rightEnd={game?.right_end}
                  onPlayPosition={(pos) => {
                    if (selectedPiece && isMyTurn()) {
                      playPiece(pos);
                    }
                  }}
                />
              </div>
              
              {/* Instructions */}
              {isMyTurn() && (
                <div className="text-center mb-4">
                  <p className="text-sm text-muted-foreground">
                    {selectedPiece 
                      ? "Klike sou kote w vle jwe (goch oswa dwat)" 
                      : "Chwazi yon domino nan men w"}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* My Hand */}
      {game?.status === 'playing' && (
        <div className="p-4 border-t bg-card/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">Men w ({getMyHand().length})</p>
              {isMyTurn() && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={passTurn}
                  className="rounded-full"
                  data-testid="pass-btn"
                >
                  <Hand className="w-4 h-4 mr-2" />
                  Pase
                </Button>
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 justify-center flex-wrap">
              {getMyHand().map(piece => (
                <DominoPiece
                  key={piece.id}
                  piece={piece}
                  selected={selectedPiece?.id === piece.id}
                  disabled={!isMyTurn() || !canPlayPiece(piece)}
                  onClick={() => {
                    if (isMyTurn() && canPlayPiece(piece)) {
                      setSelectedPiece(selectedPiece?.id === piece.id ? null : piece);
                      
                      // If it's the first move, play immediately
                      if (game.board.length === 0) {
                        playPiece('first');
                      }
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
